import { useState } from 'react';
import { Alert, App, Button, Card, Divider, InputNumber, Space, Switch, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';

export interface CallSettings {
  reconnectGraceSeconds: number;
  fallbackMaxDistinctListeners: number;
  fallbackMaxAttemptsPerListener: number;
  fallbackMaxRequestDurationSec: number;
  /** Seconds a ring to a dummy (placeholder) listener lasts before fallback moves on. */
  dummyAttemptTimeoutSec: number;
  /** Free Calls: a normal user's first-ever call starts with a free window. */
  freeCallsEnabled: boolean;
  /** Free Calls: length of that window, seconds. */
  freeCallDurationSec: number;
  /** Automatic call when a normal user enters the app (first entry / 0 coins). */
  autoCallEnabled: boolean;
  /** Minutes between two automatic call offers to the same user. */
  autoCallCooldownMin: number;
  /** Video privacy blur: the listener's video opens blurred for the first 5 s. */
  videoBlurEnabled: boolean;
  /** How long a listener's ring lasts before the call auto-rejects. */
  acceptWindowSec: number;
  /** Connected seconds before the caller's paid minutes start. */
  callerBillDelaySec: number;
  /** Connected seconds before the listener starts earning. */
  listenerEarnDelaySec: number;
  /** Seconds into each paid minute at which the caller's pro-rata charge
   *  becomes the full per-minute rate. */
  billingThresholdSec: number;
  /** Partial last minute: from paid minute 2 on, a minute may start on less
   *  than the full rate and the call runs until the coins run out. Absent on
   *  an older API (the section is hidden). */
  partialLastMinuteEnabled?: boolean;
  /** Buddy-card busy progress (countdown + bar on a busy buddy's card).
   *  Display only. The three busy-progress fields are absent on an older API
   *  (the section is hidden). */
  busyProgressEnabled?: boolean;
  /** Cap what a busy card shows at `busyProgressCapMin`. */
  busyProgressCapEnabled?: boolean;
  /** The most minutes a busy card shows. */
  busyProgressCapMin?: number;
}

/** Allowed ranges — the server enforces the same bounds and clamps on read. */
export const CALL_SETTING_LIMITS = {
  reconnectGraceSeconds: { min: 5, max: 20 },
  fallbackMaxDistinctListeners: { min: 1, max: 10 },
  fallbackMaxAttemptsPerListener: { min: 1, max: 3 },
  // Entered in minutes (0.5–5), stored in seconds (30–300).
  fallbackMaxRequestDurationSec: { min: 30, max: 300 },
  dummyAttemptTimeoutSec: { min: 1, max: 60 },
  freeCallDurationSec: { min: 1, max: 600 },
  autoCallCooldownMin: { min: 1, max: 1440 },
  acceptWindowSec: { min: 5, max: 60 },
  callerBillDelaySec: { min: 0, max: 30 },
  listenerEarnDelaySec: { min: 0, max: 30 },
  billingThresholdSec: { min: 1, max: 60 },
  busyProgressCapMin: { min: 1, max: 120 },
} as const;

/**
 * What a call of `seconds` costs under threshold `t` at `rate` coins/min — the
 * server's rule (call-billing.ts callerChargeCoins), used only for the worked
 * example under the field: full minutes, plus the last minute pro-rata
 * (floored to whole coins, minimum 1 coin) while it is below the threshold.
 */
export function exampleCallCost(seconds: number, rate: number, t: number): number {
  const done = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (s === 0) return done * rate;
  return s >= t ? (done + 1) * rate : done * rate + Math.max(1, Math.floor((rate * s) / 60));
}

type NumericKey = keyof typeof CALL_SETTING_LIMITS;

/**
 * Client-side validation of the values about to be saved (the server enforces
 * the same rules and rejects anything else): whole numbers inside the allowed
 * range — so a free-call duration is always greater than 0, never negative,
 * never fractional, never above the maximum. Returns one message per bad field.
 */
export function validateCallSettings(changes: Partial<CallSettings>): Partial<Record<NumericKey, string>> {
  const errors: Partial<Record<NumericKey, string>> = {};
  for (const k of Object.keys(CALL_SETTING_LIMITS) as NumericKey[]) {
    const v = changes[k];
    if (v === undefined) continue;
    const { min, max } = CALL_SETTING_LIMITS[k];
    if (typeof v !== 'number' || !Number.isFinite(v) || !Number.isInteger(v)) {
      errors[k] = 'Enter a whole number.';
    } else if (v < min || v > max) {
      errors[k] = `Must be between ${min} and ${max}.`;
    }
  }
  return errors;
}

/** The server's `{ message }` from a failed save, if there is one. */
function saveErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { message?: unknown } } })?.response?.data;
  return typeof data?.message === 'string' && data.message ? `Could not save: ${data.message}` : 'Could not save';
}

type Key = keyof CallSettings;

/** Only the fields that changed — an unchanged setting is never re-written. */
export function changedSettings(saved: CallSettings, draft: Partial<CallSettings>): Partial<CallSettings> {
  const out: Partial<Record<Key, CallSettings[Key]>> = {};
  for (const k of Object.keys(draft) as Key[]) {
    const v = draft[k];
    if (v !== undefined && v !== saved[k]) out[k] = v;
  }
  return out as Partial<CallSettings>;
}

/**
 * 1:1 call settings: the reconnect grace, and the Direct Call Fallback limits —
 * how far a direct call goes looking for a buddy when the one the caller picked
 * can't take it.
 */
export function CallSettingsCard() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { data } = useQuery({
    queryKey: ['calls', 'settings'],
    queryFn: async () => (await api.get<CallSettings>('/admin/calls/settings')).data,
  });
  const [draft, setDraft] = useState<Partial<CallSettings>>({});

  const save = useMutation({
    mutationFn: async (body: Partial<CallSettings>) => api.post('/admin/calls/settings', body),
    onSuccess: () => {
      message.success('Saved');
      setDraft({});
      void qc.invalidateQueries({ queryKey: ['calls', 'settings'] });
    },
    onError: (err) => message.error(saveErrorMessage(err)),
  });

  if (!data) return null;
  const value = <K extends Key>(k: K): CallSettings[K] => (draft[k] ?? data[k]) as CallSettings[K];
  const set = (k: NumericKey) => (n: number | null) =>
    setDraft((d) => ({ ...d, [k]: n === null ? undefined : n }));
  const setBool = (
    k:
      | 'freeCallsEnabled'
      | 'autoCallEnabled'
      | 'videoBlurEnabled'
      | 'partialLastMinuteEnabled'
      | 'busyProgressEnabled'
      | 'busyProgressCapEnabled',
  ) => (on: boolean) =>
    setDraft((d) => ({ ...d, [k]: on }));
  const changes = changedSettings(data, draft);
  const errors = validateCallSettings(changes);
  const invalid = Object.keys(errors).length > 0;
  const L = CALL_SETTING_LIMITS;
  const freeOn = value('freeCallsEnabled');
  const autoOn = value('autoCallEnabled');
  const blurOn = value('videoBlurEnabled');
  const progressOn = value('busyProgressEnabled') === true;
  const capOn = value('busyProgressCapEnabled') === true;

  return (
    <Card style={{ maxWidth: 640 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space direction="vertical">
          <span>Reconnect grace (seconds)</span>
          <InputNumber
            min={L.reconnectGraceSeconds.min}
            max={L.reconnectGraceSeconds.max}
            precision={0}
            value={value('reconnectGraceSeconds')}
            onChange={set('reconnectGraceSeconds')}
          />
        </Space>
        <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
          When a participant disappears mid-call, the other side sees &ldquo;Reconnecting…&rdquo; with this
          countdown, and the call ends only if they are not back in time. Applies to calls that
          connect after saving. Allowed {L.reconnectGraceSeconds.min}–{L.reconnectGraceSeconds.max} s,
          default 10. Only active while the <code>calls.reconnect_grace.enabled</code> flag is on.
        </Typography.Paragraph>

        <Divider style={{ margin: '4px 0' }} />
        <Typography.Title level={5} style={{ margin: 0 }}>
          Ringing Screen
        </Typography.Title>
        <Space direction="vertical">
          <span>Ring window (seconds)</span>
          <InputNumber
            min={L.acceptWindowSec.min}
            max={L.acceptWindowSec.max}
            precision={0}
            value={value('acceptWindowSec')}
            onChange={set('acceptWindowSec')}
            data-testid="accept-window-sec"
          />
        </Space>
        <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
          How long a buddy&apos;s phone rings before the call gives up and is recorded as no answer.
          Both countdowns — the caller&apos;s and the listener&apos;s — run on this. Applies to calls
          started after saving. Allowed {L.acceptWindowSec.min}–{L.acceptWindowSec.max} s, default 15.
          <br />
          A locked phone is woken by a push notification, which on some handsets takes 20 s or more,
          so a short window can mean the phone never rings at all. Raise this if buddies report
          missing calls while online.
        </Typography.Paragraph>

        <Divider style={{ margin: '4px 0' }} />
        <Typography.Title level={5} style={{ margin: 0 }}>
          Connect Delays
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
          The first seconds of a connected call, before the money starts. Each call keeps the values
          saved here at the moment it started, so editing these never changes a call already in
          progress. Both default to 0 — the caller is billed and the listener earns from the first
          connected second, on video and audio alike.
        </Typography.Paragraph>
        <Space direction="vertical">
          <span>Caller billing delay (seconds)</span>
          <InputNumber
            min={L.callerBillDelaySec.min}
            max={L.callerBillDelaySec.max}
            precision={0}
            value={value('callerBillDelaySec')}
            onChange={set('callerBillDelaySec')}
            data-testid="caller-bill-delay"
          />
        </Space>
        <Space direction="vertical">
          <span>Listener earning delay (seconds)</span>
          <InputNumber
            min={L.listenerEarnDelaySec.min}
            max={L.listenerEarnDelaySec.max}
            precision={0}
            value={value('listenerEarnDelaySec')}
            onChange={set('listenerEarnDelaySec')}
            data-testid="listener-earn-delay"
          />
        </Space>
        {value('listenerEarnDelaySec') < value('callerBillDelaySec') && (
          <Alert
            type="info"
            showIcon
            message="The platform pays the difference"
            description={`The listener earns from second ${value('listenerEarnDelaySec')} but the caller is only billed from second ${value('callerBillDelaySec')}. Those ${value('callerBillDelaySec') - value('listenerEarnDelaySec')} seconds are paid to the listener out of platform revenue on every call.`}
          />
        )}
        {value('listenerEarnDelaySec') > value('callerBillDelaySec') && (
          <Alert
            type="warning"
            showIcon
            message="The listener earns later than the caller pays"
            description={`The caller is billed from second ${value('callerBillDelaySec')} but the listener only earns from second ${value('listenerEarnDelaySec')}. The platform keeps the difference, and the listener is paid for ${value('listenerEarnDelaySec') - value('callerBillDelaySec')} seconds less than the call lasts.`}
          />
        )}

        <Divider style={{ margin: '4px 0' }} />
        <Typography.Title level={5} style={{ margin: 0 }}>
          Call Billing Threshold
        </Typography.Title>
        <Space direction="vertical">
          <span>Call Billing Threshold (Seconds)</span>
          <InputNumber
            min={L.billingThresholdSec.min}
            max={L.billingThresholdSec.max}
            precision={0}
            value={value('billingThresholdSec')}
            onChange={set('billingThresholdSec')}
            status={errors.billingThresholdSec ? 'error' : undefined}
            data-testid="billing-threshold-sec"
          />
          {errors.billingThresholdSec && (
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              {errors.billingThresholdSec}
            </Typography.Text>
          )}
        </Space>
        <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }} data-testid="billing-threshold-help">
          Inside every minute of an audio or video call, the caller pays per second until the
          minute reaches this many seconds; from then on that minute costs the full per-minute rate
          (charged once). Each new minute starts over. Fractions of a coin are rounded down, but a used minute
          always costs at least 1 coin. Applies
          to calls started after saving. Allowed {L.billingThresholdSec.min}–{L.billingThresholdSec.max} s,
          default 45. 1 = any started minute costs the full rate; 60 = pure per-second billing.
          <br />
          Example at 60 coins/min with {value('billingThresholdSec')} s: a 20 s call costs{' '}
          {exampleCallCost(20, 60, value('billingThresholdSec'))} coins, a 70 s call{' '}
          {exampleCallCost(70, 60, value('billingThresholdSec'))}, a 105 s call{' '}
          {exampleCallCost(105, 60, value('billingThresholdSec'))}.
        </Typography.Paragraph>

        {data.partialLastMinuteEnabled !== undefined && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Typography.Title level={5} style={{ margin: 0 }}>
              Partial Last Minute
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
              OFF (default): every paid minute needs the full per-minute rate to start; when the
              coins left are less than that, the call ends at the minute boundary and the leftover
              coins stay in the wallet. ON: from the second paid minute on, the call keeps going on
              whatever coins are left and is cut when they reach 0 — billed per second, capped at the
              balance. The first minute always needs the full rate. Example at 60 coins/min with 80
              coins: OFF = 1:00 call, 20 coins left · ON = 1:20 call, 0 coins left. Applies to calls in
              progress from their next minute.
            </Typography.Paragraph>
            <Space align="center">
              <Switch
                data-testid="partial-last-minute-enabled"
                checked={value('partialLastMinuteEnabled') === true}
                onChange={setBool('partialLastMinuteEnabled')}
              />
              <span>{value('partialLastMinuteEnabled') ? 'ON' : 'OFF'}</span>
            </Space>
          </>
        )}

        <Divider style={{ margin: '4px 0' }} />
        <Typography.Title level={5} style={{ margin: 0 }}>
          Direct Call Fallback Settings
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
          When the buddy a caller picked declines, doesn&apos;t answer or is unavailable, the call
          rings the next buddy in the feed order — one at a time, never two at once — until someone
          answers or a limit below is reached. Failed rings cost the caller nothing and pay no one.
        </Typography.Paragraph>

        <Space direction="vertical">
          <span>Maximum distinct listeners</span>
          <InputNumber
            data-testid="fallback-max-distinct"
            min={L.fallbackMaxDistinctListeners.min}
            max={L.fallbackMaxDistinctListeners.max}
            precision={0}
            value={value('fallbackMaxDistinctListeners')}
            onChange={set('fallbackMaxDistinctListeners')}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Most different buddies one call may ring. A buddy skipped because they were offline or
            busy doesn&apos;t count. {L.fallbackMaxDistinctListeners.min}–{L.fallbackMaxDistinctListeners.max},
            default 5.
          </Typography.Text>
        </Space>

        <Space direction="vertical">
          <span>Maximum attempts per listener</span>
          <InputNumber
            data-testid="fallback-max-attempts"
            min={L.fallbackMaxAttemptsPerListener.min}
            max={L.fallbackMaxAttemptsPerListener.max}
            precision={0}
            value={value('fallbackMaxAttemptsPerListener')}
            onChange={set('fallbackMaxAttemptsPerListener')}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            How many times one buddy may be rung in the same call. Everyone gets a first ring before
            anyone is rung again (A, B, C, then A, B, C). {L.fallbackMaxAttemptsPerListener.min}–
            {L.fallbackMaxAttemptsPerListener.max}, default 2.
          </Typography.Text>
        </Space>

        <Space direction="vertical">
          <span>Maximum request duration (minutes)</span>
          <InputNumber
            data-testid="fallback-max-duration"
            min={L.fallbackMaxRequestDurationSec.min / 60}
            max={L.fallbackMaxRequestDurationSec.max / 60}
            step={0.5}
            precision={1}
            value={value('fallbackMaxRequestDurationSec') / 60}
            onChange={(n) =>
              set('fallbackMaxRequestDurationSec')(n === null ? null : Math.round(n * 60))
            }
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            After this long without an answer, the call stops looking and the caller is told no
            buddy is available. 0.5–5 minutes, default 2.
          </Typography.Text>
        </Space>

        <Space direction="vertical">
          <span>Dummy listener attempt timeout (seconds)</span>
          <InputNumber
            data-testid="dummy-attempt-timeout"
            min={L.dummyAttemptTimeoutSec.min}
            max={L.dummyAttemptTimeoutSec.max}
            precision={0}
            value={value('dummyAttemptTimeoutSec')}
            onChange={set('dummyAttemptTimeoutSec')}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            How long a call to a dummy (placeholder) listener rings before moving to a real listener.
            Only dummy attempts use this; a real listener&apos;s accept window is unchanged.{' '}
            {L.dummyAttemptTimeoutSec.min}–{L.dummyAttemptTimeoutSec.max} s, default 30.
          </Typography.Text>
        </Space>

        <Divider style={{ margin: '4px 0' }} />
        <Typography.Title level={5} style={{ margin: 0 }}>
          Free Calls
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
          A normal user&apos;s first-ever call starts with a free window: the user pays nothing for it
          and the listener is still paid (platform-funded). Off = no free window — normal coin
          rules, including the balance check before a call starts. Applies to calls started after
          saving.
        </Typography.Paragraph>
        <Space align="center">
          <Switch
            data-testid="free-calls-enabled"
            checked={freeOn}
            onChange={setBool('freeCallsEnabled')}
          />
          <span>{freeOn ? 'ON' : 'OFF'}</span>
        </Space>
        <Space direction="vertical">
          <span>Free call duration (seconds)</span>
          {freeOn ? (
            <InputNumber
              data-testid="free-call-duration"
              min={L.freeCallDurationSec.min}
              max={L.freeCallDurationSec.max}
              precision={0}
              status={errors.freeCallDurationSec ? 'error' : undefined}
              value={value('freeCallDurationSec')}
              onChange={set('freeCallDurationSec')}
            />
          ) : (
            <Typography.Text type="secondary" data-testid="free-call-duration-disabled">
              Disabled
            </Typography.Text>
          )}
          {errors.freeCallDurationSec ? (
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              {errors.freeCallDurationSec}
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {L.freeCallDurationSec.min}–{L.freeCallDurationSec.max} s, default 30.
            </Typography.Text>
          )}
        </Space>

        <Divider style={{ margin: '4px 0' }} />
        <Typography.Title level={5} style={{ margin: 0 }}>
          Video Blur
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
          On = a video call opens with the listener&apos;s video blurred and a Blur button for the
          first 5 seconds, then it clears. Off = no blur at all: the video is clear from the start
          and the listener has no Blur button. Applies to calls started after saving (app version
          2.0.17 and later).
        </Typography.Paragraph>
        <Space align="center">
          <Switch
            data-testid="video-blur-enabled"
            checked={blurOn}
            onChange={setBool('videoBlurEnabled')}
          />
          <span>{blurOn ? 'ON' : 'OFF'}</span>
        </Space>

        {data.busyProgressEnabled !== undefined && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Typography.Title level={5} style={{ margin: 0 }}>
              Buddy Card Busy Progress
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
              On = a buddy who is on a call shows &ldquo;Busy · 04:35&rdquo; with a progress bar
              counting down the estimated time until they are free. Off = the card shows just
              &ldquo;Busy&rdquo;. Display only — calls and billing are not affected. Reaches cards
              already on screen at their next refresh.
            </Typography.Paragraph>
            <Space align="center">
              <Switch
                data-testid="busy-progress-enabled"
                checked={progressOn}
                onChange={setBool('busyProgressEnabled')}
              />
              <span>{progressOn ? 'ON' : 'OFF'}</span>
            </Space>
            <Space align="center">
              <Switch
                data-testid="busy-progress-cap-enabled"
                checked={capOn}
                disabled={!progressOn}
                onChange={setBool('busyProgressCapEnabled')}
              />
              <span>Limit the time shown {capOn ? 'ON' : 'OFF'}</span>
            </Space>
            <Space direction="vertical">
              <span>Time limit (minutes)</span>
              <InputNumber
                data-testid="busy-progress-cap-min"
                min={L.busyProgressCapMin.min}
                max={L.busyProgressCapMin.max}
                precision={0}
                disabled={!progressOn || !capOn}
                status={errors.busyProgressCapMin ? 'error' : undefined}
                value={value('busyProgressCapMin')}
                onChange={set('busyProgressCapMin')}
              />
              <Typography.Text type={errors.busyProgressCapMin ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                {errors.busyProgressCapMin ??
                  `A busy card never shows more than this. If the call can last longer, the card counts down from the limit; when that runs out and the buddy is still busy, it shows the real time left — or the limit again if that is still longer. Example with a 10 min limit and 25 min left: 10:00 → 0, 10:00 → 0, then 05:00 → 0. ${L.busyProgressCapMin.min}–${L.busyProgressCapMin.max} min, default 10.`}
              </Typography.Text>
            </Space>
          </>
        )}

        <Divider style={{ margin: '4px 0' }} />
        <Typography.Title level={5} style={{ margin: 0 }}>
          Automatic Call on App Entry
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
          When a normal user opens the app for the first time, or opens it with 0 coins, they are
          shown an incoming-style call from an available buddy (ringing, with Accept / Decline).
          Accept or Decline both open the coin packs — no real call starts and nothing rings the
          buddy or bills.
        </Typography.Paragraph>
        <Space align="center">
          <Switch
            data-testid="auto-call-enabled"
            checked={autoOn}
            onChange={setBool('autoCallEnabled')}
          />
          <span>{autoOn ? 'ON' : 'OFF'}</span>
        </Space>
        <Space direction="vertical">
          <span>Minimum time between offers (minutes)</span>
          <InputNumber
            data-testid="auto-call-cooldown"
            min={L.autoCallCooldownMin.min}
            max={L.autoCallCooldownMin.max}
            precision={0}
            disabled={!autoOn}
            status={errors.autoCallCooldownMin ? 'error' : undefined}
            value={value('autoCallCooldownMin')}
            onChange={set('autoCallCooldownMin')}
          />
          <Typography.Text type={errors.autoCallCooldownMin ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
            {errors.autoCallCooldownMin ??
              `One offer per user at most this often, however often the app is opened. ${L.autoCallCooldownMin.min}–${L.autoCallCooldownMin.max} min, default 30.`}
          </Typography.Text>
        </Space>

        <Alert
          type="info"
          showIcon
          message="New calls only"
          description="Each call keeps the limits it started with — saving never changes a call that is already ringing. Fallback runs only while the calls.fallback_queue.enabled feature flag is on, and only for app versions that support it; older versions ring one buddy, as before."
        />
        <Can permission="calls.settings.edit">
          <Button
            type="primary"
            data-testid="call-settings-save"
            disabled={Object.keys(changes).length === 0 || invalid}
            loading={save.isPending}
            onClick={() => save.mutate(changes)}
          >
            Save
          </Button>
        </Can>
      </Space>
    </Card>
  );
}
